import { createI18n } from 'vue-i18n'
import { Messages, Locale } from '@/types'
import { SupportedLocale, DEFAULT_LOCALE } from '@/composables/useLocaleDetection'
import enGB from './locales/en-gb'
import enUS from './locales/en-us'
import esES from './locales/es-es'
import frFR from './locales/fr-fr'
import deDE from './locales/de-de'
import itIT from './locales/it-it'
import ptPT from './locales/pt-pt'
import nlNL from './locales/nl-nl'
import skSK from './locales/sk-sk'
import ruRU from './locales/ru-ru'
import arSA from './locales/ar-sa'

export const messages: Record<string, Messages> = {
    'en-GB': enGB,
    'en-US': enUS,
    'es-ES': esES,
    'fr-FR': frFR,
    'de-DE': deDE,
    'it-IT': itIT,
    'pt-PT': ptPT,
    'nl-NL': nlNL,
    'sk-SK': skSK,
    'ru-RU': ruRU,
    'ar-SA': arSA,
}

/**
 * Get initial locale from localStorage or browser
 * This is a synchronous function for i18n initialization
 */
function getInitialLocale(): SupportedLocale {
    try {
        const stored = localStorage.getItem('user_locale_preference')
        if (stored && messages[stored as SupportedLocale]) {
            return stored as SupportedLocale
        }
    } catch (error) {
        // Ignore localStorage errors
    }

    // Try to detect from browser language
    if (typeof navigator !== 'undefined') {
        const browserLang = navigator.language || (navigator as any).userLanguage
        if (browserLang) {
            const langLower = browserLang.toLowerCase()
            // Map common browser languages to supported locales
            if (langLower.startsWith('ar')) return 'ar-SA'
            if (langLower.startsWith('sk')) return 'sk-SK'
            if (langLower.startsWith('ru')) return 'ru-RU'
            if (langLower.startsWith('es')) return 'es-ES'
            if (langLower.startsWith('fr')) return 'fr-FR'
            if (langLower.startsWith('de')) return 'de-DE'
            if (langLower.startsWith('en-gb') || langLower === 'en') return 'en-GB'
            if (langLower.startsWith('en')) return 'en-US'
        }
    }

    return DEFAULT_LOCALE
}

export const i18n = createI18n({
    locale: getInitialLocale(),
    fallbackLocale: DEFAULT_LOCALE,
    legacy: false,
    globalInjection: true,
    messages,
})

/**
 * Set the locale for the i18n instance
 */
export function setLocale(locale: SupportedLocale): void {
    if (messages[locale]) {
        i18n.global.locale.value = locale
    } else {
        i18n.global.locale.value = DEFAULT_LOCALE
    }
}

// Type declaration for vue-i18n
declare module 'vue-i18n' {
    export interface DefineLocaleMessage extends Messages {}
}
